import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import PhotoTags from "components/Photos/PhotoTool/Gallery/PhotoTags";
import { getPhotoNumbersSetting, getPhotoSizeSetting, getPhotoTagSetting, PhotoSizeMeta, photoSizeMetaData, photoTagHoverSettings, photoToolPhotoNumberingOptions } from "components/Photos/PhotoTool/Shared";
import { UserPreference } from "interfaces/Mueller.Business.Models";
import { GalleryPhotoPhotoProps } from "components/Photos/PhotoTool/Gallery/index";
import { Chip } from "@mui/material";
import { PHOTO_WRITE_UNRESTRICTED } from "typeConstants/rights";
import { userHasRight } from "selectors/rights";

/* eslint-disable react/prop-types */ // Using TypeScript interface for props validation

interface GalleryPhotoProps {
  sortingPhotos: boolean,
  photo: GalleryPhotoPhotoProps,
  margin: number,
  index: number,
  onClick?: (e: React.MouseEvent<HTMLImageElement, MouseEvent>, obj: {index: number, photo: GalleryPhotoPhotoProps} ) => void,
  multiSelect?: boolean,
  isSelected: (id: string) => boolean,
  customerSendEmailView: boolean, // for when the photo tool is being loaded into the "send email" view in case actions that customers use
  addSelectedPhotoTag: (photoTag: string) => any,
  // state
  preferences: UserPreference[],
  canEditPhotos: boolean
}

const GalleryPhoto = (props: GalleryPhotoProps) => {

  const photoRef = useRef();
  const imgRef = useRef<HTMLImageElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [photoNumbersSetting, setPhotoNumbersSetting] = useState<string>();
  const [photoTagsSetting, setPhotoTagsSetting] = useState<string>();
  const [photoSizeSetting, setPhotoSizeSetting] = useState<PhotoSizeMeta>();

  useEffect(() => {
    // lazy loading implementation
    setIsInView(false);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(entry.target);
        }
      },
      {
        rootMargin: '100px 0px',
        threshold: 0.1
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [props.sortingPhotos]);

  useEffect(() => {
    if(props.photo) {
      setPhotoNumbersSetting(getPhotoNumbersSetting(props.preferences));
      setPhotoTagsSetting(getPhotoTagSetting(props.preferences));
      setPhotoSizeSetting(props.customerSendEmailView ? photoSizeMetaData.SMALL : getPhotoSizeSetting(props.preferences));
    }
      
  },[props.photo]);

  if (!props.photo)
    return (<></>);

  const isDisplayingPhotoNumbers = !props.customerSendEmailView && photoNumbersSetting && (photoNumbersSetting === photoToolPhotoNumberingOptions.SHOW_NUMBERS_ALWAYS || photoNumbersSetting === photoToolPhotoNumberingOptions.SHOW_ON_HOVER);
  const isDisplayingPhotoTags = (photoTagsSetting === photoTagHoverSettings.SHOW || photoTagsSetting === photoTagHoverSettings.HOVER)// Make sure they have the settings for displaying tags
    && (!props.multiSelect || props.multiSelect && props.canEditPhotos); // If it's not multi-select, always fine. If it's multi-select, only show tags to users who can edit photos (internal users)
  const photoNumberClassName = `${photoNumbersSetting === photoToolPhotoNumberingOptions.SHOW_NUMBERS_ALWAYS ? " always-show" : ""}`;

  const renderImage = () => {
    const commonProps = {
      ...props.photo,
      ref: imgRef,
      src: isInView ? props.photo.src : null,
    };

    if (!props.sortingPhotos) {
      return (
        <img
          {...commonProps}
          onClick={(e) => props.onClick(e, { index: props.index, photo: props.photo })}
        />
      );
    } 
    else if (!props.multiSelect && props.sortingPhotos) {
      return (<img {...commonProps} />);
    }
    
    return null;
  };

  return (
    <div
      className="gallery-photo"
      ref={photoRef}
    >
      {/* I don't really like using inline styling but it's the easiest way to set the dimensions dynamically since we have to account for aspect ratio */}
      <div className="gallery-photo-content" style={{ width: photoSizeSetting?.dimensions.width, height: photoSizeSetting?.dimensions.height }}>
        {renderImage()}
        {<>
            {isDisplayingPhotoTags &&
              <PhotoTags
                photoTagsArray={props.photo?.alt?.length ? props.photo.alt.split(",") : null}
                photoWidth={props.photo.width}
                photoHeight={props.photo.height}
                addSelectedPhotoTag={props.addSelectedPhotoTag}
              />}
            {isDisplayingPhotoNumbers &&
              <Chip
                key={props.photo.photoid}
                className={`photo-chip gallery-photo-number-chip${photoNumberClassName}`}
                label={props.photo.index + 1}
              />}
          </>}
        {props.multiSelect && props.isSelected(props.photo.photoid) &&
          <i className="md-icon material-icons">check_circle</i>
        }
      </div>
    </div>
    );
};

const mapStateToProps = state => ({
  preferences: state.preferences,
  canEditPhotos: userHasRight(PHOTO_WRITE_UNRESTRICTED)(state),
});

export default connect(mapStateToProps)(GalleryPhoto);
